/**
 * Déduplication des appels CONCURRENTS à un même chargement.
 *
 * Le motif fautif, présent dans la plupart des stores :
 *
 *     if (dejaCharge) return;              // le garde
 *     await Service.getAll(...);           // 2 s pendant lesquelles dejaCharge vaut encore false
 *     dejaCharge = true;                   // posé APRÈS
 *
 * Le garde protège les appels séquentiels, pas les appels concurrents : tout appelant qui arrive
 * dans la fenêtre de l'await franchit le test et lance sa propre requête. Mesure du 31/08 sur le
 * Suivi de fabrication (STE, 8 lignes) : 23 × GET /Boar/search?IsPresent=false à 3 798 ko, soit
 * 84 Mo et 26 s d'attente sur les connexions du navigateur.
 *
 * `inFlight` ne mémorise QUE la promesse en cours : l'entrée est retirée dès qu'elle se règle,
 * succès ou échec. Ce n'est donc pas un cache — aucune donnée périmée ne peut survivre à un
 * changement de centre, et une coupure réseau passagère ne condamne pas les appels suivants.
 * La clé doit porter tout ce qui distingue deux chargements différents (centre, fenêtre de dates).
 */
const pending = new Map<string, Promise<unknown>>();

export function inFlight<T>(key: string, factory: () => Promise<T>): Promise<T> {
    const existing = pending.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    // finally et non then : une requête en échec doit libérer la clé, sinon le store resterait
    // définitivement non chargé après une coupure passagère.
    const promise = factory().finally(() => {
        pending.delete(key);
    });
    pending.set(key, promise);
    return promise;
}

/** Remise à zéro — réservée aux tests, pour isoler les cas les uns des autres. */
export function clearInFlight(): void {
    pending.clear();
}
